import { UnfoldLess, UnfoldMore } from "@mui/icons-material";
import { Button } from "@mui/material";
import { LoaderCircle } from "lucide-react";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFallbackIcon } from "../../../ui/useFallbackIcon";
import { ToolApproval, useToolApproval } from "../../ToolApproval";

const RevealToolDetailsContext = createContext<() => void>(() => {});

export interface ToolCallFrameProps {
  /** The tool title to display in the header */
  title: ReactNode;
  /** Use a non-button title wrapper when the title contains its own interactive control. */
  titleContainsInteractiveContent?: boolean;
  /** Optional integration icon URL to display left of the title */
  icon?: string | string[] | null;
  /** Whether the tool is currently loading/executing */
  isLoading?: boolean;
  /** Whether the tool has an error */
  hasError?: boolean;
  /** Whether the user rejected the tool call. */
  isRejected?: boolean;
  /** Whether the content is expandable */
  isExpandable?: boolean;
  /** Whether to expand by default (only applies when isExpandable is true) */
  expandedByDefault?: boolean;
  /** Keep nested approval controls mounted while their parent is collapsed. */
  keepContentMounted?: boolean;
  /** Whether there is a tool result (used to determine when to show expanded content) */
  hasToolResult?: boolean;
  /** Whether there is preview content before a final tool result exists */
  hasContent?: boolean;
  /** Actions to render in the header (e.g., Apply button) */
  actions?: ReactNode;
  /** Approval controls shown below the tool box, including while collapsed. */
  footer?: ReactNode;
  /** The content to render when expanded */
  children?: ReactNode;
  compact?: boolean;
}

export const ToolCallFrame = ({
  title,
  titleContainsInteractiveContent = false,
  icon,
  isLoading = false,
  hasError = false,
  isRejected = false,
  isExpandable = true,
  expandedByDefault = false,
  keepContentMounted = false,
  hasToolResult = false,
  hasContent = false,
  actions,
  footer,
  children,
  compact = false,
}: ToolCallFrameProps) => {
  const approval = useToolApproval();
  const revealParent = useContext(RevealToolDetailsContext);
  const toolRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);
  useEffect(() => {
    if (!highlighted) return;
    const timeout = setTimeout(() => setHighlighted(false), 2000);
    return () => clearTimeout(timeout);
  }, [highlighted]);
  const canShowContent = hasToolResult || hasContent || !!footer;
  const shouldExpandByDefault = expandedByDefault && canShowContent;
  const [isExpanded, setIsExpanded] = useState(shouldExpandByDefault);
  const hasAutoExpanded = useRef(shouldExpandByDefault);
  const {
    activeIcon,
    isVisible: isIconVisible,
    handleError,
  } = useFallbackIcon(icon);

  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

  // Open once when streamed preview content or a final result first becomes available.
  useEffect(() => {
    if (expandedByDefault && canShowContent && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      setIsExpanded(true);
    }
  }, [canShowContent, expandedByDefault]);

  const revealDetails = useCallback(() => {
    revealParent();
    setIsExpanded(true);
  }, [revealParent]);
  const revealChanges = () => {
    revealDetails();
    setHighlighted(true);
    requestAnimationFrame(() => {
      toolRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      toolRef.current?.focus({ preventScroll: true });
    });
  };
  const approvalBadge = isRejected ? (
    <span
      role="status"
      aria-label="Tool status"
      className="shrink-0 whitespace-nowrap text-sm text-gray-500"
    >
      Rejected
    </span>
  ) : approval ? (
    <Button
      size="small"
      color="warning"
      className="shrink-0 whitespace-nowrap"
      disabled={!!approval.decision}
      onClick={(event) => {
        event.stopPropagation();
        approval.select();
      }}
    >
      {approval.decision
        ? approval.decision.type === "reject"
          ? "Rejected"
          : "Approved"
        : "Needs approval"}
    </Button>
  ) : null;
  const showExpandableHeader = isExpandable && canShowContent;
  const shouldRenderIcon = !!activeIcon && isIconVisible;
  const expandableTitleClassName = `flex min-w-0 flex-1 items-center gap-2 p-0 m-0 ${compact ? "text-[13px]" : "text-sm"} transition-colors duration-200 ease-in-out cursor-pointer border-0 outline-none focus:outline-none bg-transparent text-left ${
    hasError
      ? "text-red-500 hover:text-red-700"
      : "text-gray-500 hover:text-gray-700"
  }`;
  const expandableTitleContent = (
    <>
      {shouldRenderIcon && (
        <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded border border-gray-300 bg-white p-0.5">
          <img
            src={activeIcon ?? undefined}
            alt=""
            className="w-full h-full object-contain"
            onError={handleError}
          />
        </span>
      )}
      <span
        className={`min-w-0 flex-1 truncate leading-5 ${compact ? "text-[13px]" : "text-sm"}`}
      >
        {title}
      </span>
      {isLoading && <LoaderCircle className="h-5 w-5 shrink-0 animate-spin" />}
    </>
  );
  const expandableTitle = titleContainsInteractiveContent ? (
    <span className={expandableTitleClassName}>{expandableTitleContent}</span>
  ) : (
    <button
      type="button"
      className={expandableTitleClassName}
      aria-expanded={compact ? isExpanded : undefined}
      onClick={compact ? toggleExpanded : undefined}
    >
      {expandableTitleContent}
    </button>
  );
  const expandCollapseButton = (
    <button
      type="button"
      aria-expanded={isExpanded}
      aria-label={isExpanded ? "Collapse tool details" : "Expand tool details"}
      onClick={(e) => {
        e.stopPropagation();
        toggleExpanded();
      }}
      className="flex-shrink-0 flex items-center justify-center cursor-pointer border-0 outline-none focus:outline-none bg-transparent"
    >
      {isExpanded ? (
        <UnfoldLess className="h-4 w-4 text-gray-500 rotate-180" />
      ) : (
        <UnfoldMore className="h-4 w-4 text-gray-500" />
      )}
    </button>
  );
  const headerActions = actions ? (
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex items-center gap-2"
    >
      {actions}
    </div>
  ) : null;

  return (
    <div className="space-y-2">
      <div
        ref={toolRef}
        tabIndex={-1}
        data-approval-highlighted={highlighted}
        role="group"
        aria-label="Tool details"
        className={`scroll-mt-2 overflow-hidden ${compact ? "rounded-none border-0 bg-transparent" : "rounded-md border border-solid bg-white"} ${highlighted ? "outline outline-2 outline-amber-400 -outline-offset-2" : ""} ${
          hasError ? "border-red-300" : "border-gray-300"
        }`}
      >
        {/* Clickable Header - Functions as the expand/collapse button */}
        {showExpandableHeader ? (
          <div
            className={`flex min-h-[30px] w-full items-center justify-between gap-2 ${compact ? "py-1 bg-transparent hover:bg-black/[.04]" : "cursor-pointer p-2 bg-gray-50"}`}
            onClick={compact ? undefined : toggleExpanded}
          >
            {expandableTitle}
            <div className="flex shrink-0 items-center gap-2">
              {approvalBadge}
              {headerActions}
              {compact ? (
                <span aria-hidden="true" className="flex shrink-0 items-center">
                  {isExpanded ? (
                    <UnfoldLess className="h-4 w-4 text-gray-500 rotate-180" />
                  ) : (
                    <UnfoldMore className="h-4 w-4 text-gray-500" />
                  )}
                </span>
              ) : (
                expandCollapseButton
              )}
            </div>
          </div>
        ) : (
          /* Non-clickable header for items without expandable content */
          <div
            className={`flex items-center justify-between gap-2 ${compact ? "py-1 bg-transparent" : "p-2 bg-gray-50"}`}
          >
            <div
              className={`flex min-w-0 flex-1 items-center gap-2 text-sm ${
                hasError ? "text-red-600" : "text-gray-500"
              }`}
            >
              {shouldRenderIcon && (
                <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded border border-gray-300 bg-white p-0.5">
                  <img
                    src={activeIcon ?? undefined}
                    alt=""
                    className="w-full h-full object-contain"
                    onError={handleError}
                  />
                </span>
              )}
              <span
                className={`min-w-0 flex-1 truncate leading-5 ${compact ? "text-[13px]" : "text-sm"}`}
              >
                {title}
              </span>
              {isLoading && (
                <LoaderCircle className="h-5 w-5 shrink-0 animate-spin" />
              )}
            </div>
            {/* Actions can still be shown for non-expandable items if needed */}
            {(actions || approvalBadge) && (
              <div className="flex shrink-0 items-center gap-2">
                {approvalBadge}
                {actions}
              </div>
            )}
          </div>
        )}

        {/* Content - Expandable frames show it when expanded; fixed frames show it inline. */}
        {canShowContent &&
          (keepContentMounted || !isExpandable || isExpanded) &&
          children && (
            <div
              className={
                compact
                  ? "ml-[18px] border-0 border-l border-solid border-[#e2e8f0] pl-3 py-1"
                  : "border-t border-gray-300 p-2"
              }
              hidden={isExpandable && !isExpanded}
            >
              <RevealToolDetailsContext.Provider value={revealDetails}>
                {children}
              </RevealToolDetailsContext.Provider>
            </div>
          )}
      </div>
      {approval ? (
        <ToolApproval title={title} onViewChanges={revealChanges}>
          {footer}
        </ToolApproval>
      ) : (
        footer && (
          <div
            role="group"
            aria-label="Tool approval"
            className="flex flex-wrap items-center gap-2 rounded-md border border-solid border-gray-200 bg-gray-50 p-2"
          >
            {footer}
          </div>
        )
      )}
    </div>
  );
};

ToolCallFrame.displayName = "ToolCallFrame";
